import { useMemo, useState } from 'react'

import { formatClock, formatDayClock, formatInt } from '../../lib/format.ts'
import type { AlertPayload, AlertSeverity } from '../../lib/sse/types.ts'
import { Panel } from '../primitives/Panel.tsx'
import { StatusDot } from '../primitives/StatusDot.tsx'

const SEVERITY_BORDER: Record<AlertSeverity, string> = {
  info: 'border-l-accent',
  warning: 'border-l-warn',
  critical: 'border-l-crit',
}

const SEVERITY_TEXT: Record<AlertSeverity, string> = {
  info: 'text-fg-muted',
  warning: 'text-warn',
  critical: 'text-crit',
}

type Filter = 'all' | 'actionable' | 'critical'

const FILTERS: ReadonlyArray<{ key: Filter; label: string }> = [
  { key: 'all', label: 'tudo' },
  { key: 'actionable', label: 'acionável' },
  { key: 'critical', label: 'crítico' },
]

const RANK: Record<AlertSeverity, number> = { info: 0, warning: 1, critical: 2 }

/** Older than this and the day matters, not just the clock. */
const SAME_DAY_MS = 12 * 60 * 60 * 1000

/**
 * Alert rail.
 *
 * A tail, not an archive: the stream buffer is capped upstream and this renders
 * whatever is in it, newest first. Severity is carried by a left rule, the
 * status dot and the timestamp colour together, so it survives both a
 * colour-blind operator and a glance from across the room.
 *
 * The entry animation is deliberate and narrow — an alert appearing *is* a state
 * change, which is the one thing this console animates. The numbers never do.
 */
export function AlertRail({
  alerts,
  className,
}: {
  alerts: readonly AlertPayload[]
  className?: string
}) {
  const [filter, setFilter] = useState<Filter>('all')
  const now = Date.now()

  const counts = useMemo(() => {
    const tally: Record<AlertSeverity, number> = { info: 0, warning: 0, critical: 0 }
    for (const alert of alerts) tally[alert.severity] += 1
    return tally
  }, [alerts])

  const visible = useMemo(() => {
    const floor = filter === 'critical' ? RANK.critical : filter === 'actionable' ? RANK.warning : 0
    return alerts.filter((alert) => RANK[alert.severity] >= floor)
  }, [alerts, filter])

  const newest = alerts[0]

  return (
    <Panel
      title="Alertas"
      className={className}
      flush
      meta={
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            <StatusDot tone="crit" />
            <span className="tnum">{formatInt(counts.critical)}</span>
          </span>
          <span className="flex items-center gap-1">
            <StatusDot tone="warn" />
            <span className="tnum">{formatInt(counts.warning)}</span>
          </span>
          <span className="flex items-center gap-1">
            <StatusDot tone="accent" />
            <span className="tnum">{formatInt(counts.info)}</span>
          </span>
        </span>
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div
          role="group"
          aria-label="Filtrar alertas por severidade"
          className="flex shrink-0 border-b border-line-subtle"
        >
          {FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={filter === option.key}
              onClick={() => setFilter(option.key)}
              className={`flex-1 border-r border-line-subtle px-2 py-1 text-micro uppercase tracking-label last:border-r-0 ${
                filter === option.key
                  ? 'bg-inset text-fg'
                  : 'text-fg-muted hover:text-fg-secondary'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Announced without moving focus; the rail itself is not a live region,
            or every filter change would be read out in full. */}
        <p className="sr-only" role="status" aria-live="polite">
          {newest === undefined
            ? 'Nenhum alerta recebido.'
            : `Alerta ${newest.severity}: ${newest.message}`}
        </p>

        {visible.length === 0 ? (
          <p className="p-3 text-sm text-fg-muted">
            {alerts.length === 0
              ? 'Nenhum alerta desde a conexão.'
              : 'Nenhum alerta nesse filtro.'}
          </p>
        ) : (
          <ul className="min-h-0 flex-1 overflow-y-auto">
            {visible.map((alert) => {
              const occurred = Date.parse(alert.occurred_at)
              const sameDay = Number.isFinite(occurred) && now - occurred < SAME_DAY_MS
              return (
                <li
                  key={alert.id}
                  className={`alert-enter border-b border-l-2 border-b-line-subtle px-2.5 py-1.5 ${SEVERITY_BORDER[alert.severity]}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className={`truncate text-micro uppercase tracking-label ${SEVERITY_TEXT[alert.severity]}`}
                    >
                      {alert.kind}
                    </span>
                    <span className="shrink-0 tnum font-mono text-micro text-fg-muted">
                      {sameDay ? formatClock(alert.occurred_at) : formatDayClock(alert.occurred_at)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm leading-snug text-fg">{alert.message}</p>
                  {alert.tenant_name != null && alert.tenant_name !== '' && (
                    <p className="mt-0.5 truncate text-micro text-fg-muted">{alert.tenant_name}</p>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Panel>
  )
}
